import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  FaPills, FaPlus, FaCopy, FaTrash, FaDownload, FaEdit,
  FaArrowUp, FaArrowDown, FaTimes, FaSave, FaPlusCircle
} from 'react-icons/fa';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_TEMPLATE = [
  { title: 'Bienvenida y Descargo de Responsabilidad', content: '' },
  { title: 'Farmacología a Utilizar', content: '' },
  { title: 'Protectores', content: '' },
  { title: 'Reguladores Hormonales', content: '' },
  { title: 'Seguimiento de 6 Semanas', content: '' },
];

function authHeaders() {
  const token = localStorage.getItem('deisycfit_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function PharmacologyCycleTab() {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | cycle object
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newClient, setNewClient] = useState('');
  const [savingDownload, setSavingDownload] = useState(null);

  const fetchCycles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pharmacology-cycles`, { headers: authHeaders() });
      setCycles(res.data?.cycles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar los ciclos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCycles();
  }, [fetchCycles]);

  const handleCreate = async () => {
    if (!newTitle.trim()) {
      toast.error('Ponle un nombre al ciclo');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/admin/pharmacology-cycles`,
        { title: newTitle.trim(), client_name: newClient.trim() || null, duration_weeks: 6 },
        { headers: authHeaders() }
      );
      toast.success('Ciclo creado');
      setShowCreate(false);
      setNewTitle('');
      setNewClient('');
      await fetchCycles();
      setEditing(res.data?.cycle || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear');
    }
  };

  const handleDuplicate = async (cycle) => {
    try {
      await axios.post(`${API}/admin/pharmacology-cycles/${cycle.id}/duplicate`, {}, { headers: authHeaders() });
      toast.success('Ciclo duplicado');
      fetchCycles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo duplicar');
    }
  };

  const handleDelete = async (cycle) => {
    if (!window.confirm(`¿Eliminar "${cycle.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/pharmacology-cycles/${cycle.id}`, { headers: authHeaders() });
      toast.success('Ciclo eliminado');
      fetchCycles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const handleDownload = async (cycle) => {
    setSavingDownload(cycle.id);
    try {
      const res = await axios.get(`${API}/admin/pharmacology-cycles/${cycle.id}/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(cycle.title || 'ciclo').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (e) {
      toast.error('No se pudo descargar el PDF');
    } finally {
      setSavingDownload(null);
    }
  };

  if (editing) {
    return (
      <CycleEditor
        cycle={editing}
        onClose={() => setEditing(null)}
        onSaved={(updated) => {
          setEditing(updated);
          fetchCycles();
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="border-2 border-pink-500/40 bg-gradient-to-r from-pink-900/30 to-purple-900/30">
        <CardContent className="py-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-pink-500 text-white text-xl">
                <FaPills />
              </div>
              <div>
                <h3 className="font-bold text-pink-300 text-lg">Ciclos de Farmacología</h3>
                <p className="text-sm text-pink-200/70">Biblioteca privada de ciclos personalizados. Descarga el PDF y envíaselo a tu clienta manualmente.</p>
              </div>
            </div>
            <Button
              onClick={() => setShowCreate(true)}
              className="bg-pink-500 hover:bg-pink-600 text-white"
              data-testid="new-pharmacology-cycle-btn"
            >
              <FaPlus className="mr-2" /> Nuevo Ciclo
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Listado */}
      {loading ? (
        <p className="text-gray-400 text-center py-8">Cargando ciclos...</p>
      ) : cycles.length === 0 ? (
        <Card className="border border-white/10 bg-gray-900/50">
          <CardContent className="py-10 text-center">
            <FaPills className="mx-auto text-5xl text-pink-400/40 mb-3" />
            <p className="text-gray-300 mb-1">Aún no tienes ciclos creados</p>
            <p className="text-sm text-gray-500 mb-4">Crea uno con la plantilla por defecto y rellena las páginas que necesites.</p>
            <Button onClick={() => setShowCreate(true)} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="new-pharmacology-cycle-empty-btn">
              <FaPlus className="mr-2" /> Crear primer ciclo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cycles.map((cycle) => (
            <Card key={cycle.id} className="border border-pink-500/20 bg-gray-900/60 hover:border-pink-500/50 transition-all" data-testid={`pharmacology-cycle-${cycle.id}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2 gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-white truncate">{cycle.title}</h4>
                    {cycle.client_name && (
                      <p className="text-xs text-gray-400 mt-0.5">Para: {cycle.client_name}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {cycle.pages?.length || 0} páginas • {cycle.duration_weeks || 6} semanas
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-3">
                  <Button size="sm" variant="outline" className="text-white border-white/20 hover:bg-white/10" onClick={() => setEditing(cycle)} data-testid={`edit-cycle-${cycle.id}`}>
                    <FaEdit className="mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" className="text-pink-300 border-pink-500/30 hover:bg-pink-500/10" onClick={() => handleDownload(cycle)} disabled={savingDownload === cycle.id} data-testid={`download-cycle-${cycle.id}`}>
                    <FaDownload className="mr-1" /> {savingDownload === cycle.id ? '...' : 'PDF'}
                  </Button>
                  <Button size="sm" variant="outline" className="text-blue-300 border-blue-400/30 hover:bg-blue-500/10" onClick={() => handleDuplicate(cycle)} data-testid={`duplicate-cycle-${cycle.id}`}>
                    <FaCopy className="mr-1" /> Duplicar
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-300 border-red-400/30 hover:bg-red-500/10" onClick={() => handleDelete(cycle)} data-testid={`delete-cycle-${cycle.id}`}>
                    <FaTrash />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Diálogo Crear */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent style={{ backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}>
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FaPills className="text-pink-400" /> Nuevo Ciclo de Farmacología
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Se creará con la plantilla por defecto (Bienvenida, Farmacología, Protectores, Reguladores, Seguimiento). Podrás editar todo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm text-gray-300 mb-1 block">Nombre del ciclo *</label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Ej: Ciclo Definición Otoño 2026"
                className="bg-white/5 border-white/20 text-white"
                data-testid="new-cycle-title-input"
              />
            </div>
            <div>
              <label className="text-sm text-gray-300 mb-1 block">Nombre de la clienta (opcional)</label>
              <Input
                value={newClient}
                onChange={(e) => setNewClient(e.target.value)}
                placeholder="Aparecerá en la portada del PDF"
                className="bg-white/5 border-white/20 text-white"
                data-testid="new-cycle-client-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-white/20 text-white hover:bg-white/10">
              Cancelar
            </Button>
            <Button onClick={handleCreate} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="confirm-create-cycle-btn">
              <FaPlus className="mr-2" /> Crear y editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============= EDITOR =============

function CycleEditor({ cycle, onClose, onSaved }) {
  const [title, setTitle] = useState(cycle.title || '');
  const [clientName, setClientName] = useState(cycle.client_name || '');
  const [durationWeeks, setDurationWeeks] = useState(cycle.duration_weeks || 6);
  const [pages, setPages] = useState(
    (cycle.pages && cycle.pages.length > 0
      ? cycle.pages
      : DEFAULT_TEMPLATE.map((p, i) => ({ id: `page-${i}`, order: i, ...p }))
    ).map((p, i) => ({ ...p, order: i }))
  );
  const [saving, setSaving] = useState(false);

  const updatePage = (idx, patch) => {
    setPages((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const addPage = () => {
    setPages((prev) => [
      ...prev,
      { id: `page-new-${Date.now()}`, order: prev.length, title: 'Nueva sección', content: '' },
    ]);
  };

  const removePage = (idx) => {
    if (!window.confirm('¿Eliminar esta sección?')) return;
    setPages((prev) => prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, order: i })));
  };

  const movePage = (idx, dir) => {
    setPages((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((p, i) => ({ ...p, order: i }));
    });
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('El ciclo debe tener un nombre');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        client_name: clientName.trim() || null,
        duration_weeks: parseInt(durationWeeks) || 6,
        pages: pages.map((p, i) => ({
          id: p.id,
          order: i,
          title: p.title || `Sección ${i + 1}`,
          content: p.content || '',
        })),
      };
      const res = await axios.put(`${API}/admin/pharmacology-cycles/${cycle.id}`, payload, {
        headers: authHeaders(),
      });
      toast.success('Cambios guardados');
      onSaved(res.data?.cycle || { ...cycle, ...payload });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      await handleSave();
      const res = await axios.get(`${API}/admin/pharmacology-cycles/${cycle.id}/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(title || 'ciclo').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (e) {
      toast.error('No se pudo descargar el PDF');
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <Card className="border border-pink-500/30 bg-gray-900/70">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose} className="border-white/20 text-white hover:bg-white/10" data-testid="back-to-cycles-btn">
              <FaTimes className="mr-2" /> Volver
            </Button>
            <div>
              <p className="text-xs text-gray-400">Editando ciclo</p>
              <p className="text-white font-semibold">{title || 'Sin título'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-cycle-btn">
              <FaSave className="mr-2" /> {saving ? 'Guardando...' : 'Guardar'}
            </Button>
            <Button onClick={handleDownload} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="download-cycle-pdf-btn">
              <FaDownload className="mr-2" /> Descargar PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Datos generales */}
      <Card className="border border-white/10 bg-gray-900/50">
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Nombre del ciclo</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-white/5 border-white/20 text-white" data-testid="edit-cycle-title" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Nombre de la clienta (opcional)</label>
            <Input value={clientName} onChange={(e) => setClientName(e.target.value)} className="bg-white/5 border-white/20 text-white" data-testid="edit-cycle-client" />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Duración (semanas)</label>
            <Input type="number" min={1} max={52} value={durationWeeks} onChange={(e) => setDurationWeeks(e.target.value)} className="bg-white/5 border-white/20 text-white" data-testid="edit-cycle-weeks" />
          </div>
        </CardContent>
      </Card>

      {/* Páginas */}
      <div className="space-y-3">
        {pages.map((page, idx) => (
          <Card key={page.id || idx} className="border border-pink-500/20 bg-gray-900/60" data-testid={`page-editor-${idx}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-pink-400 bg-pink-500/10 px-2 py-1 rounded">SECCIÓN {idx + 1}</span>
                <Input
                  value={page.title}
                  onChange={(e) => updatePage(idx, { title: e.target.value })}
                  placeholder="Título de la sección"
                  className="bg-white/5 border-white/20 text-white font-semibold flex-1"
                  data-testid={`page-title-${idx}`}
                />
                <Button size="sm" variant="outline" onClick={() => movePage(idx, -1)} disabled={idx === 0} className="border-white/20 text-white hover:bg-white/10" title="Subir">
                  <FaArrowUp />
                </Button>
                <Button size="sm" variant="outline" onClick={() => movePage(idx, 1)} disabled={idx === pages.length - 1} className="border-white/20 text-white hover:bg-white/10" title="Bajar">
                  <FaArrowDown />
                </Button>
                <Button size="sm" variant="outline" onClick={() => removePage(idx)} className="text-red-300 border-red-400/30 hover:bg-red-500/10" title="Eliminar">
                  <FaTrash />
                </Button>
              </div>
              <Textarea
                value={page.content}
                onChange={(e) => updatePage(idx, { content: e.target.value })}
                placeholder="Contenido de la sección. Usa saltos de línea para separar párrafos."
                rows={6}
                className="bg-white/5 border-white/20 text-white resize-y"
                data-testid={`page-content-${idx}`}
              />
            </CardContent>
          </Card>
        ))}
        <Button onClick={addPage} variant="outline" className="w-full border-dashed border-pink-500/40 text-pink-300 hover:bg-pink-500/10" data-testid="add-page-btn">
          <FaPlusCircle className="mr-2" /> Agregar otra sección
        </Button>
      </div>
    </div>
  );
}
